import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useRouter } from 'next/router'

import { useLatest } from 'ahooks'

import type { ImageProps } from '~/components/atoms/Image/Image'
import { PortfolioCollectionVariant } from '~/components/blocks/PortfolioItemDetails/PortfolioItemDetails'
import { PortfolioItemDetailsTheme } from '~/components/blocks/PortfolioItemDetails/PortfolioItemDetails'
import { VerifyVariants } from '~/components/blocks/VerifyDialog/VerifyDialog'
import { modalTypes } from '~/components/modalLoader'
import { ReportTheme } from '~/components/report/types'
import { useUserPortfolio } from '~/containers/AppContainer/PortfolioProvider'
import { useAuthV2 } from '~/contexts/AuthContext'
import { GlobalStoreContext } from '~/contexts/GlobalStoreContext'
import {
  GlobalStoreProps,
  GlobalToastActions,
} from '~/contexts/GlobalStoreReducer'
import { TicketTypes } from '~/data/forms/shared/interfaces/Form'
import { AUTH_CONFIG } from '~/pages/api/auth/auth-config'
import { buildItemFromCreatedTicket } from '~/services/buildItemFromCreatedTicket'
import { serializePortfolioArtworkData } from '~/services/serializePortfolioArtworkData'
import {
  analyticsEventTypes,
  multiPostHogEventPusher,
} from '~/utils/analyticsPusher'
import { getRouterPath } from '~/utils/getRouterPath'
import { isArtworkInPortfolio } from '~/utils/isArtworkInPortfolio'
import { getLocalStorage } from '~/utils/webStorage'

import type { PortfolioArtworkDetails } from './usAddToMyPortfolioActions'
import { useFeatureFlag } from './useFeatureFlag'
import useGeoLocation from './useLocation'
import useOpenEnquiryActiveModal from './useOpenEnquiryActiveModal'
import usePortfolioTicketCreator from './usePortfolioTicketCreator'
import usePortfolioTicketUpdater from './usePortfolioTicketUpdater'

const getAddToWatchParam = (id: string) => `addtowatch_${id}`
const getAddToPortfolioParam = (id: string) => `addtoportfolio_${id}`

declare global {
  interface Window {
    __myArtBroker__: {
      executedActions: Set<string>
      executedUrlParams: Set<string>
      completedArtworkActions: Set<string>
    }
  }
}

// global tracking for executed actions - shared across all instances
// help to alleviate issues with multiple components trying to process the same action
// as artwork cards are in a list, rerender causes multiple actions to be triggered
// after the user logs in and the modal is closed
if (typeof window !== 'undefined') {
  window.__myArtBroker__ = window.__myArtBroker__ || {
    executedActions: new Set(),
    executedUrlParams: new Set(),
    completedArtworkActions: new Set(),
  }
}

const useIsolatedPortfolioActions = (
  artworkId: string,
  artworkImage: ImageProps,
  artistId: string,
  artworkDetails?: PortfolioArtworkDetails | null
) => {
  const { activeCurrencyIso } = useGeoLocation()
  const { user, showAuthDialog } = useAuthV2()
  const router = useRouter()
  const showExpert = router.query?.showExpert === '1'
  const { stateDispatch } = useContext(GlobalStoreContext)
  const { doCreate } = usePortfolioTicketCreator()
  const { openActiveEnquiryModal } = useOpenEnquiryActiveModal()
  const { doUpdate } = usePortfolioTicketUpdater()
  const [isAddingPortfolio, setIsAddingPortfolio] = useState<boolean>(false)
  const [isAddingWatching, setIsAddingWatching] = useState<boolean>(false)
  const { portfolioDetails } = useUserPortfolio()
  // Modal props are snapshotted into the global store when a modal opens, so
  // callbacks stored there capture stale closures. This ref always points at
  // the latest portfolio data so lookups made from those callbacks stay fresh.
  const latestPortfolioDetails = useLatest(portfolioDetails)

  const watchParamKey = getAddToWatchParam(artworkId)
  const portfolioParamKey = getAddToPortfolioParam(artworkId)

  const { getModalDialogFlag } = useFeatureFlag()
  const modalDialogFlag = getModalDialogFlag()

  // Calculate portfolio ticket IDs
  const isPortfolioTicket = useMemo(
    () => isArtworkInPortfolio(artworkId, 'portfolio', portfolioDetails),
    [artworkId, portfolioDetails]
  )

  const isWatchingTicket = useMemo(
    () => isArtworkInPortfolio(artworkId, 'watching', portfolioDetails),
    [artworkId, portfolioDetails]
  )

  // track which artwork ID is currently being processed
  const processingArtworkId = useRef<string | null>(null)

  // Use ref to store pending action to avoid stale closure issues
  const pendingActionRef = useRef<'watch' | 'portfolio' | null>(null)

  // Use ref to track waiting state to persist across re-renders
  const isWaitingForPhoneRef = useRef<boolean>(false)

  const isMountedRef = useRef(true)
  const hasProcessedAfterLogin = useRef(false)
  const previousUserId = useRef<string | null>(null)
  const isExecutingRef = useRef(false)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Clear execution tracking when user logs in to allow retry
  useEffect(() => {
    if (user?.id && user.id !== previousUserId.current) {
      // User just logged in
      previousUserId.current = user.id
      hasProcessedAfterLogin.current = false

      // Clear ALL execution tracking when user logs in
      window.__myArtBroker__.executedUrlParams.clear()
      window.__myArtBroker__.executedActions.clear()
      if (window.__myArtBroker__.completedArtworkActions) {
        window.__myArtBroker__.completedArtworkActions.clear()
      }

      if (process.env.NEXT_PUBLIC_ENV === 'development') {
        console.log(
          `Cleared all execution tracking on user login for ${user.id}`
        )
      }
    }
  }, [user?.id])

  const generatePortfolioItemDetails = useCallback(
    (
      newPortfolioDetails?: any,
      variant: 'portfolio' | 'watching' = 'portfolio'
    ) => {
      const id = Number(artworkId.split('-')[1])

      // Read from the ref so callbacks stored in modal props never search
      // a stale snapshot of the user's portfolio
      const userPortfolio = newPortfolioDetails || latestPortfolioDetails.current

      // Look in the appropriate collection based on variant
      const collection =
        variant === 'watching'
          ? userPortfolio?.portfolioItems?.watching
          : userPortfolio?.portfolioItems?.portfolio

      const artworkItem = collection?.find(
        (artwork: { artwork_id: number }) => artwork.artwork_id === id
      )

      if (!artworkItem) {
        console.log(`Artwork not found in ${variant}`)
        return
      }

      const portfolioItemDetails = serializePortfolioArtworkData(
        artworkItem,
        artworkDetails
      )

      return portfolioItemDetails
    },
    [artworkDetails, artworkId, latestPortfolioDetails]
  )

  const handleViewPortfolioClick = useCallback(
    (variant: 'portfolio' | 'watching', itemOverride?: any) => {
      const collectionVariant =
        variant === 'watching'
          ? PortfolioCollectionVariant['Watching']
          : PortfolioCollectionVariant['Myportfolio']

      // Prefer the freshly built item (e.g. from a just-created ticket) over
      // a lookup, which can miss items the portfolio hasn't refetched yet
      const portfolioItemDetails =
        itemOverride || generatePortfolioItemDetails(undefined, variant)

      if (!portfolioItemDetails) {
        console.warn(
          `Unable to resolve portfolio item details for ${artworkId}`
        )
        return
      }

      stateDispatch({
        type: GlobalStoreProps.UPDATE_PREVENT_PAGE_SCROLL,
        payload: true,
      })

      // Match the dashboard theme mapping so the tabbed slideout is shown
      const itemDetailsTheme =
        variant === 'watching'
          ? PortfolioItemDetailsTheme.Portfolio
          : PortfolioItemDetailsTheme.MyCollection

      // Open the portfolio slideout without closing the modal
      stateDispatch({
        type: GlobalStoreProps.UPDATE_PORTFOLIO_SLIDEOUT_STATE,
        payload: {
          isOpen: true,
          item: portfolioItemDetails,
          variant: collectionVariant,
          theme: itemDetailsTheme,
          useDarkTheme: false,
          showPortfolioHeader: true,
        },
      })
    },
    [generatePortfolioItemDetails, stateDispatch, artworkId]
  )

  const clearQueryParam = useCallback(() => {
    const routerQuery = { ...router.query }

    delete routerQuery[watchParamKey]
    delete routerQuery[portfolioParamKey]

    const updatedQuery = routerQuery
    delete updatedQuery['lang']

    router.replace(
      {
        pathname: getRouterPath(router),
        query: updatedQuery,
      },
      undefined,
      { shallow: true }
    )

    if (isMountedRef.current) {
      setIsAddingWatching(false)
      setIsAddingPortfolio(false)
    }
  }, [router, watchParamKey, portfolioParamKey])

  const openToast = useCallback(
    (message: string, portfolioItemDetails?: any) => {
      const toastType = GlobalToastActions.addToPortfolio

      const portfolioDetailsModal = portfolioItemDetails
        ? {
            portfolioItemDetails: portfolioItemDetails,
            collectionVariant: PortfolioCollectionVariant['Myportfolio'],
            itemDetailsTheme: PortfolioItemDetailsTheme.Artwork,
            isReducedWidth: true,
          }
        : undefined

      stateDispatch({
        type: GlobalStoreProps.UPDATE_TOAST_STATE,
        payload: {
          isOpen: true,
          action: toastType,
          image: artworkImage,
          message: message,
          includePortfolioLink: !portfolioDetailsModal,
          useMyPortfolioModal: portfolioItemDetails ? true : false,
          infoWindowProps: portfolioDetailsModal,
        },
      })
    },
    [artworkImage, stateDispatch]
  )

  const AddToHandler = useCallback(
    async (
      ticketType: TicketTypes,
      toastMessage: string,
      gaEventName: string,
      showToast = true
    ): Promise<any> => {
      // ensure we're working with the correct artwork (only check if processingArtworkId is already set)
      if (
        processingArtworkId.current &&
        processingArtworkId.current !== artworkId &&
        process.env.NEXT_PUBLIC_ENV === 'development'
      ) {
        console.log(
          `Skipping action for ${processingArtworkId.current} - expected ${artworkId}`
        )
        return
      }

      // create unique request ID for this action
      // prevents duplicate actions from being processed on re-renders on logins
      const requestId = `${artworkId}_${ticketType}_${Date.now()}`
      if (
        window.__myArtBroker__.executedActions.has(requestId) &&
        process.env.NEXT_PUBLIC_ENV === 'development'
      ) {
        console.log(`Request ${requestId} already processed globally, skipping`)
        return
      }

      window.__myArtBroker__.executedActions.add(requestId)

      setTimeout(() => {
        window.__myArtBroker__.executedActions.delete(requestId)
      }, 5000)

      const currentPage = `${location.protocol}//${location.host}${location.pathname}`
      const properties = {
        artwork: parseInt(artworkId.split('-')[1]),
        ticket_type: ticketType,
        call_to_action_url: currentPage,
        artist: artistId,
      }

      try {
        const updateRomeo = await doCreate(properties, ticketType)

        if (process.env.NEXT_PUBLIC_ENV === 'development') {
          console.log(`doCreate response for ${artworkId}:`, updateRomeo)
        }

        // Always show feedback even if component unmounted
        if (updateRomeo?.success) {
          const portfolioItemDetails = buildItemFromCreatedTicket(
            updateRomeo.ticketId as string,
            artworkDetails
          )

          if (ticketType === TicketTypes.owned) {
            // Commenting out toast for "own it" action - may be re-enabled in future
            // openToast(toastMessage, portfolioItemDetails)

            // Open the portfolio drawer immediately for "own it" action
            stateDispatch({
              type: GlobalStoreProps.UPDATE_PORTFOLIO_SLIDEOUT_STATE,
              payload: {
                isOpen: true,
                item: portfolioItemDetails,
                variant: PortfolioCollectionVariant['Myportfolio'],
                theme: PortfolioItemDetailsTheme.MyCollection,
                useDarkTheme: false,
                showPortfolioHeader: true,
              },
            })
          } else if (showToast) {
            openToast(toastMessage)
          }

          return portfolioItemDetails
        }

        if (updateRomeo?.status === 'enquiry active') {
          // enquiry already in place so launch the there is already a an enquiry in place modal.
          openActiveEnquiryModal()
          // Return a special value to indicate enquiry active
          return { enquiryActive: true }
        }

        // Log unsuccessful response
        if (process.env.NEXT_PUBLIC_ENV === 'development') {
          console.log(`Unsuccessful response for ${artworkId}:`, updateRomeo)
        }

        multiPostHogEventPusher(
          [
            {
              eventType: gaEventName,
            },
          ],
          {
            eventType: analyticsEventTypes.gaEvent,
            data: {
              gaEventName: gaEventName,
            },
          }
        )

        // Return undefined if not successful
        return undefined
      } catch (error) {
        console.error(`Error processing request ${requestId}:`, error)
        throw error
      }
    },
    [
      artworkId,
      artistId,
      doCreate,
      artworkDetails,
      openToast,
      openActiveEnquiryModal,
      stateDispatch,
    ]
  )

  const addToWatchAction = useCallback(
    async (showToast = true) => {
      if (isAddingWatching) {
        return
      }

      processingArtworkId.current = artworkId
      setIsAddingWatching(true)

      try {
        const portfolioItemDetails = await AddToHandler(
          TicketTypes.watching,
          'Artwork added to watchlist',
          'PDP Add To My Watchlist Link',
          showToast
        )
        return portfolioItemDetails
      } catch (error) {
        console.error('Error in addToWatchAction:', error)
        throw error
      } finally {
        // Always reset state, regardless of mount status
        setIsAddingWatching(false)
        processingArtworkId.current = null
      }
    },
    [AddToHandler, artworkId, isAddingWatching]
  )

  const addToPortfolioAction = useCallback(async () => {
    if (isAddingPortfolio) {
      return
    }

    processingArtworkId.current = artworkId
    setIsAddingPortfolio(true)

    try {
      await AddToHandler(
        TicketTypes.owned,
        'Artwork added to portfolio',
        'PDP Add To MyPortfolio Link'
      )
    } catch (error) {
      console.error('Error in addToPortfolioAction:', error)
      throw error
    } finally {
      // Always reset state, regardless of mount status
      setIsAddingPortfolio(false)
      processingArtworkId.current = null
    }
  }, [AddToHandler, artworkId, isAddingPortfolio])

  const noUserAction = useCallback(
    (paramKey: string, modal?: string) => {
      if (modal) {
        const returningUserData = getLocalStorage(AUTH_CONFIG.RETURNING_USER)
        const component = returningUserData ? 'authModal' : modal
        stateDispatch({
          type: GlobalStoreProps.UPDATE_MODAL_STATE,
          payload: {
            isOpen: true,
            component: component,
            isDrawer: true,
            props: {
              isReducedWidth: true,
              theme: !returningUserData ? ReportTheme.quaternary : undefined,
              image: artworkImage,
              showMpModalHeader: returningUserData ? true : false,
            },
          },
        })
      } else {
        showAuthDialog('', true)
      }
      const updatedQuery: any = { ...router.query, [paramKey]: 1 }
      const routerQuery = updatedQuery
      delete routerQuery['lang']

      router.replace(
        {
          pathname: getRouterPath(router),
          query: routerQuery,
        },
        undefined,
        { shallow: true }
      )
    },
    [artworkImage, router, showAuthDialog, stateDispatch]
  )

  const openVerifyModal = useCallback(() => {
    const updatedQuery = {
      ...router.query,
      verify: 'true',
      verifyVariant: VerifyVariants.AccountSetup,
    }

    router.push(
      {
        pathname: getRouterPath(router),
        query: updatedQuery,
      },
      undefined,
      { shallow: true }
    )

    stateDispatch({
      type: GlobalStoreProps.UPDATE_MODAL_STATE,
      payload: {
        isOpen: true,
        component: modalTypes.verifyModal,
        content: null,
        isDrawer: true,
        props: {
          returnToCurrent: true,
          isReducedWidth: true,
          variant: VerifyVariants.AccountSetup,
          image: artworkImage,
          theme: undefined,
          onPhoneSubmitted: async () => {
            // Wait a bit for the user object to update
            setTimeout(async () => {
              // Execute the pending action directly
              if (pendingActionRef.current === 'watch') {
                const portfolioItemDetails = await addToWatchAction(false) // Don't show toast

                if (portfolioItemDetails) {
                  // Open the modal in success state
                  stateDispatch({
                    type: GlobalStoreProps.UPDATE_MODAL_STATE,
                    payload: {
                      isOpen: true,
                      component: modalTypes.confirmPortfolioActionModal,
                      isDrawer: false,
                      props: {
                        theme: undefined,
                        image: artworkImage,
                        modifierClass: 'confirmPortfolioActionModal',
                        artworkDetails: portfolioItemDetails,
                        artworkTitle: artworkDetails?.title || '',
                        artworkId: artworkId,
                        isPortfolioTicket: !!isPortfolioTicket,
                        isWatchingTicket: true,
                        watchlistSuccess: true,
                        closeHandler: () => {
                          stateDispatch({
                            type: GlobalStoreProps.UPDATE_MODAL_STATE,
                            payload: {
                              isOpen: false,
                              isDrawer: false,
                              component: null,
                              content: null,
                              props: null,
                            },
                          })
                        },
                        onViewInPortfolio: (
                          variant: 'portfolio' | 'watching'
                        ) => {
                          // Pass the freshly built item directly — the global
                          // portfolio data may not include the new ticket yet
                          handleViewPortfolioClick(variant, portfolioItemDetails)
                        },
                      },
                    },
                  })
                }
              } else if (pendingActionRef.current === 'portfolio') {
                await addToPortfolioAction()
              }

              // Clear everything
              pendingActionRef.current = null
              isWaitingForPhoneRef.current = false
              clearQueryParam()
            }, 500)
          },
        },
      },
    })
  }, [
    router,
    stateDispatch,
    artworkImage,
    artworkId,
    artworkDetails,
    isPortfolioTicket,
    handleViewPortfolioClick,
    addToWatchAction,
    addToPortfolioAction,
    clearQueryParam,
  ])

  const addToWatchListHandler = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation()
        e.preventDefault()
      }

      if (!user) {
        noUserAction(
          watchParamKey,
          modalDialogFlag !== 'control' ? 'authWatchlist' : undefined
        )
        return
      }

      // Check for phone number
      if (!user.phoneNumber) {
        pendingActionRef.current = 'watch'
        isWaitingForPhoneRef.current = true
        openVerifyModal()
        return
      }

      addToWatchAction()
    },
    [
      addToWatchAction,
      modalDialogFlag,
      noUserAction,
      user,
      watchParamKey,
      openVerifyModal,
    ]
  )

  const addToMyPortfolioHandler = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation()
        e.preventDefault()
      }

      if (!user) {
        noUserAction(
          portfolioParamKey,
          modalDialogFlag !== 'control' ? 'authAddToPortfolio' : undefined
        )
        return
      }

      // Check for phone number
      if (!user.phoneNumber) {
        pendingActionRef.current = 'portfolio'
        isWaitingForPhoneRef.current = true
        openVerifyModal()
        return
      }

      addToPortfolioAction()
    },
    [
      user,
      addToPortfolioAction,
      noUserAction,
      portfolioParamKey,
      modalDialogFlag,
      openVerifyModal,
    ]
  )

  useEffect(() => {
    if (!user?.id) return

    const hasWatchParam = watchParamKey in router.query
    const hasPortfolioParam = portfolioParamKey in router.query
    if (!hasWatchParam && !hasPortfolioParam) {
      return
    }

    // Ensure completedArtworkActions exists
    if (!window.__myArtBroker__.completedArtworkActions) {
      window.__myArtBroker__.completedArtworkActions = new Set()
    }

    // Check if this artwork has already completed this action
    const completedKey = `${artworkId}_${hasWatchParam ? 'watch' : 'portfolio'}_${user.id}`
    if (window.__myArtBroker__.completedArtworkActions.has(completedKey)) {
      if (process.env.NEXT_PUBLIC_ENV === 'development') {
        console.log(
          `${artworkId} already completed ${hasWatchParam ? 'watch' : 'portfolio'} action, clearing params`
        )
      }
      // Clear the query params if they're still present
      clearQueryParam()
      return
    }

    // If we're waiting for phone submission, don't execute yet
    if (isWaitingForPhoneRef.current) {
      return
    }

    // Prevent concurrent executions
    if (isExecutingRef.current) {
      if (process.env.NEXT_PUBLIC_ENV === 'development') {
        console.log(`Already executing for ${artworkId}, skipping`)
      }
      return
    }

    // Create a global execution key that includes user ID to prevent cross-user issues
    const globalExecutionKey = `${user.id}_${hasWatchParam ? 'watch' : 'portfolio'}`

    // Check if ANY artwork is already processing this type of action for this user
    if (window.__myArtBroker__.executedUrlParams.has(globalExecutionKey)) {
      if (process.env.NEXT_PUBLIC_ENV === 'development') {
        console.log(
          `Another artwork is already processing ${hasWatchParam ? 'watch' : 'portfolio'} action for user ${user.id}, skipping ${artworkId}`
        )
      }
      return
    }

    const executeActions = async () => {
      // Set executing flag
      isExecutingRef.current = true

      // Mark globally that this type of action is being processed
      window.__myArtBroker__.executedUrlParams.add(globalExecutionKey)

      if (process.env.NEXT_PUBLIC_ENV === 'development') {
        console.log(
          `${artworkId} is executing ${hasWatchParam ? 'watch' : 'portfolio'} action for user ${user.id}`,
          {
            hasWatchParam,
            hasPortfolioParam,
            userId: user?.id,
            userPhone: !!user?.phoneNumber,
          }
        )
      }

      try {
        // Check if user has phone number
        if (!user?.phoneNumber) {
          // Store which action was attempted
          if (hasWatchParam) {
            pendingActionRef.current = 'watch'
          } else if (hasPortfolioParam) {
            pendingActionRef.current = 'portfolio'
          }

          // Set flag to indicate we're waiting for phone
          isWaitingForPhoneRef.current = true

          // Open verify modal
          openVerifyModal()

          // Clean up global execution tracking
          window.__myArtBroker__.executedUrlParams.delete(globalExecutionKey)
          isExecutingRef.current = false
          return
        }

        let actionSucceeded = false

        if (hasWatchParam) {
          try {
            if (process.env.NEXT_PUBLIC_ENV === 'development') {
              console.log(`Executing addToWatchAction for ${artworkId}`)
            }

            const portfolioItemDetails = await addToWatchAction(false) // Don't show toast

            if (
              portfolioItemDetails &&
              !(portfolioItemDetails as any).enquiryActive
            ) {
              actionSucceeded = true

              // Mark this artwork as having completed this action
              const completedKey = `${artworkId}_watch_${user?.id}`
              if (!window.__myArtBroker__.completedArtworkActions) {
                window.__myArtBroker__.completedArtworkActions = new Set()
              }
              window.__myArtBroker__.completedArtworkActions.add(completedKey)

              // Clear query params IMMEDIATELY to prevent re-execution
              clearQueryParam()

              if (process.env.NEXT_PUBLIC_ENV === 'development') {
                console.log(
                  `Opening modal for ${artworkId} with portfolio details`,
                  portfolioItemDetails
                )
              }

              // Open the modal in success state
              stateDispatch({
                type: GlobalStoreProps.UPDATE_MODAL_STATE,
                payload: {
                  isOpen: true,
                  component: modalTypes.confirmPortfolioActionModal,
                  isDrawer: false,
                  props: {
                    theme: undefined,
                    image: artworkImage,
                    modifierClass: 'confirmPortfolioActionModal',
                    artworkDetails: portfolioItemDetails,
                    artworkTitle: artworkDetails?.title || '',
                    artworkId: artworkId,
                    isPortfolioTicket: !!isPortfolioTicket,
                    isWatchingTicket: true,
                    watchlistSuccess: true,
                    closeHandler: () => {
                      stateDispatch({
                        type: GlobalStoreProps.UPDATE_MODAL_STATE,
                        payload: {
                          isOpen: false,
                          isDrawer: false,
                          component: null,
                          content: null,
                          props: null,
                        },
                      })
                    },
                    onViewInPortfolio: (variant: 'portfolio' | 'watching') => {
                      // Pass the freshly built item directly — the global
                      // portfolio data may not include the new ticket yet
                      handleViewPortfolioClick(variant, portfolioItemDetails)
                    },
                  },
                },
              })
            } else {
              if ((portfolioItemDetails as any)?.enquiryActive) {
                actionSucceeded = true // Mark as succeeded since modal was shown

                // Mark this artwork as having completed this action (enquiry active counts as complete)
                const completedKey = `${artworkId}_watch_${user?.id}`
                if (!window.__myArtBroker__.completedArtworkActions) {
                  window.__myArtBroker__.completedArtworkActions = new Set()
                }
                window.__myArtBroker__.completedArtworkActions.add(completedKey)

                if (process.env.NEXT_PUBLIC_ENV === 'development') {
                  console.log(`Enquiry active modal shown for ${artworkId}`)
                }
              } else {
                if (process.env.NEXT_PUBLIC_ENV === 'development') {
                  console.log(
                    `No portfolio item details returned for ${artworkId}`
                  )
                }
              }
              // Clear params even if no details returned or enquiry active
              clearQueryParam()
            }
          } catch (actionError) {
            console.error(
              `Error in addToWatchAction for ${artworkId}:`,
              actionError
            )
          }
        }

        if (hasPortfolioParam) {
          try {
            if (process.env.NEXT_PUBLIC_ENV === 'development') {
              console.log(`Executing addToPortfolioAction for ${artworkId}`)
            }
            await addToPortfolioAction()
            actionSucceeded = true

            // Mark this artwork as having completed this action
            const completedKey = `${artworkId}_portfolio_${user?.id}`
            if (!window.__myArtBroker__.completedArtworkActions) {
              window.__myArtBroker__.completedArtworkActions = new Set()
            }
            window.__myArtBroker__.completedArtworkActions.add(completedKey)

            // Clear params immediately
            clearQueryParam()
          } catch (actionError) {
            console.error(
              `Error in addToPortfolioAction for ${artworkId}:`,
              actionError
            )
          }
        }

        // Only clear query params here if action didn't succeed
        // (successful actions clear params after modal opens)
        if (!actionSucceeded && isMountedRef.current) {
          if (process.env.NEXT_PUBLIC_ENV === 'development') {
            console.log(`Clearing query params for ${artworkId}, action failed`)
          }
          clearQueryParam()
        }
      } catch (error) {
        console.error(`Error executing URL actions for ${artworkId}:`, error)

        // Clear query params even on error
        if (isMountedRef.current) {
          clearQueryParam()
        }
      } finally {
        // Always clean up
        window.__myArtBroker__.executedUrlParams.delete(globalExecutionKey)
        isExecutingRef.current = false

        if (process.env.NEXT_PUBLIC_ENV === 'development') {
          console.log(
            `${artworkId} finished processing, cleaned up global execution tracking`
          )
        }
      }
    }

    // Add a small random delay to reduce race conditions when multiple artworks load
    const delay = Math.random() * 100
    const timer = setTimeout(() => {
      executeActions()
    }, delay)

    return () => {
      clearTimeout(timer)
    }
  }, [
    user?.id,
    user?.phoneNumber,
    router.query,
    watchParamKey,
    portfolioParamKey,
    artworkId,
    addToWatchAction,
    addToPortfolioAction,
    clearQueryParam,
    openVerifyModal,
  ])

  // Removed the phone number effect since we're handling execution in the callback

  const openConfirmPortfolioActionModal = useCallback(
    (
      action: string,
      artworkTitle: string,
      isPortfolioTicketParam: boolean,
      isWatchingTicketParam: boolean
    ) => {
      stateDispatch({
        type: GlobalStoreProps.UPDATE_MODAL_STATE,
        payload: {
          isOpen: true,
          component: modalTypes.confirmPortfolioActionModal,
          isDrawer: false,
          props: {
            theme: undefined,
            image: artworkImage,
            modifierClass: 'confirmPortfolioActionModal',
            allowDrawerPriority: true, // Allow portfolio drawer to appear above this modal
            portfolioAction: action,
            artworkTitle: artworkTitle,
            artworkId: artworkId,
            isPortfolioTicket: isPortfolioTicketParam,
            isWatchingTicket: isWatchingTicketParam,
            watchlistSuccess: false,
            artworkDetails: artworkDetails,
            onViewInPortfolio: (variant: 'portfolio' | 'watching') => {
              // Open the portfolio drawer with the appropriate variant (without closing the modal)
              handleViewPortfolioClick(variant)
            },
            onSave: async (selectedAction: 'addtowatch' | 'addtoportfolio') => {
              // Check if user is authenticated
              if (!user) {
                // Close the modal first
                stateDispatch({
                  type: GlobalStoreProps.UPDATE_MODAL_STATE,
                  payload: {
                    isOpen: false,
                    isDrawer: false,
                    component: null,
                    content: null,
                    props: null,
                  },
                })

                // Show auth dialog based on action
                if (selectedAction === 'addtowatch') {
                  noUserAction(
                    watchParamKey,
                    modalDialogFlag !== 'control' ? 'authWatchlist' : undefined
                  )
                } else if (selectedAction === 'addtoportfolio') {
                  noUserAction(
                    portfolioParamKey,
                    modalDialogFlag !== 'control'
                      ? 'authAddToPortfolio'
                      : undefined
                  )
                }
                return
              }

              // Check if user has phone number (required for SSO users)
              if (!user.phoneNumber) {
                // Store the pending action to execute after phone verification
                pendingActionRef.current =
                  selectedAction === 'addtowatch' ? 'watch' : 'portfolio'
                isWaitingForPhoneRef.current = true

                // Close the confirmation modal first
                stateDispatch({
                  type: GlobalStoreProps.UPDATE_MODAL_STATE,
                  payload: {
                    isOpen: false,
                    isDrawer: false,
                    component: null,
                    content: null,
                    props: null,
                  },
                })

                // Open verify modal to collect phone number
                openVerifyModal()
                return
              }

              // Execute the action if user is authenticated and has phone
              if (selectedAction === 'addtowatch') {
                // If switching from portfolio to watchlist, remove the portfolio ticket
                // Note: Using the actual ticket IDs from the hook closure, not the modal params
                if (
                  typeof isPortfolioTicket === 'number' &&
                  isPortfolioTicket
                ) {
                  await doUpdate(isPortfolioTicket, { is_archived: true })
                }
                const portfolioItemDetails = await addToWatchAction(false)

                // Small delay to ensure state updates propagate
                await new Promise(resolve => setTimeout(resolve, 50))

                // Update modal props to show success state instead of closing
                stateDispatch({
                  type: GlobalStoreProps.UPDATE_MODAL_STATE,
                  payload: {
                    isOpen: true,
                    component: modalTypes.confirmPortfolioActionModal,
                    isDrawer: false,
                    props: {
                      theme: undefined,
                      image: artworkImage,
                      modifierClass: 'confirmPortfolioActionModal',
                      artworkDetails: portfolioItemDetails,
                      allowDrawerPriority: true,
                      portfolioAction: action,
                      artworkTitle: artworkTitle,
                      artworkId: artworkId,
                      isPortfolioTicket: isPortfolioTicketParam,
                      isWatchingTicket: true, // Now it's in watchlist
                      watchlistSuccess: true,
                      onViewInPortfolio: (
                        variant: 'portfolio' | 'watching'
                      ) => {
                        // Pass the freshly built item directly — the global
                        // portfolio data may not include the new ticket yet
                        handleViewPortfolioClick(variant, portfolioItemDetails)
                      },
                    },
                  },
                })
              } else if (selectedAction === 'addtoportfolio') {
                // If switching from watchlist to portfolio, remove the watching ticket
                // Note: Using the actual ticket IDs from the hook closure, not the modal params
                if (typeof isWatchingTicket === 'number' && isWatchingTicket) {
                  await doUpdate(isWatchingTicket, { is_archived: true })
                }
                await addToPortfolioAction()

                // Small delay to ensure state updates propagate
                await new Promise(resolve => setTimeout(resolve, 50))

                // Close the modal after portfolio action
                stateDispatch({
                  type: GlobalStoreProps.UPDATE_MODAL_STATE,
                  payload: {
                    isOpen: false,
                    isDrawer: false,
                    component: null,
                    content: null,
                    props: null,
                  },
                })
              }
            },
          },
        },
      })
    },
    [
      user,
      artworkId,
      artworkImage,
      modalDialogFlag,
      stateDispatch,
      addToWatchAction,
      addToPortfolioAction,
      noUserAction,
      watchParamKey,
      portfolioParamKey,
      openVerifyModal,
      handleViewPortfolioClick,
      isPortfolioTicket,
      isWatchingTicket,
      doUpdate,
    ]
  )

  return {
    addToMyPortfolioHandler,
    addToWatchListHandler,
    showExpert,
    user,
    isAddingWatching,
    isAddingPortfolio,
    openConfirmPortfolioActionModal,
  }
}

export default useIsolatedPortfolioActions
